import { useState } from 'react';

import type { SkillItem } from '@pages/PracticePage/model';
import { usePracticeView } from '@pages/PracticePage/model';

import { AIChatModal } from '@widgets/AIChatModal';

import { Heading } from '@shared/ui';

import { PracticeTheoryModal } from './PracticeTheoryModal';
import { SkillItemRow } from './SkillItemRow';

interface PriorityItem extends SkillItem {
  group: string;
}

interface PracticePriorityListProps {
  items: PriorityItem[];
  title?: string;
  subtitle?: string;
  showGroupLabel?: boolean;
}

export const PracticePriorityList = ({
  items,
  title = 'All Subskills',
  subtitle = 'Low scores first',
  showGroupLabel: _showGroupLabel = true,
}: PracticePriorityListProps) => {
  const { showDescriptions } = usePracticeView();
  const hasHeader = Boolean(title || subtitle);
  const [selectedSkill, setSelectedSkill] = useState<{
    item: SkillItem;
    groupTitle: string;
  } | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [initialMessage, setInitialMessage] = useState<string | undefined>(undefined);

  const handleOpenChat = (message?: string) => {
    setInitialMessage(message);
    setIsChatOpen(true);
  };

  const handleCloseChat = () => {
    setIsChatOpen(false);
    setInitialMessage(undefined);
  };

  return (
    <section className="">
      {hasHeader ? (
        <div className="flex items-center justify-between">
          {title ? (
            <Heading tag="h3" size="base" aboveCard>
              {title}
            </Heading>
          ) : null}
          {subtitle ? <span className="text-xs text-muted-foreground">{subtitle}</span> : null}
        </div>
      ) : null}
      <div className="mt-3 space-y-2">
        {items.map((item) => (
          <SkillItemRow
            key={item.title}
            item={item}
            showDescriptions={showDescriptions}
            onOpen={() => {
              setSelectedSkill({ item, groupTitle: item.group });
            }}
          />
        ))}
      </div>
      <PracticeTheoryModal
        isOpen={Boolean(selectedSkill)}
        item={selectedSkill?.item ?? null}
        groupTitle={selectedSkill?.groupTitle}
        onClose={() => {
          setSelectedSkill(null);
        }}
        onOpenChat={handleOpenChat}
      />
      <AIChatModal
        isOpen={isChatOpen}
        onClose={handleCloseChat}
        initialMessage={initialMessage}
        startNewChat
      />
    </section>
  );
};
